import { FC, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MdCampaign, MdDescription, MdHelpOutline, MdSupportAgent, MdScreenShare } from 'react-icons/md';
import Button from '@srm/ui/button';
import Menu from '@srm/ui/menu';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { RouteNames } from '@/router/router.types';
import { IListElement } from '@srm/ui/types/IListElement';
import { OPEN_TECH_SUPPORT_MENU_EVENT } from '@utils/techSupportMenu';
import { useScreenShareContext } from '@providers/ScreenShareProvider/ScreenShareContext';
import { REMOTE_CONTROL_EXCLUDE_CLASS } from '@srm/screen-sharing/light';
import { OnboardingHint } from '@srm/ui/shell/UserMenu';
import { useUi } from '@srm/ui/provider';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { isSupportShareIntroSeen, markSupportShareIntroSeen } from '@molecules/SupportShareIntro/supportShareIntroSeen';
import SupportDialog from '../SupportDialog/SupportDialog';
import ScreenShareHintCard from './ScreenShareHintCard/ScreenShareHintCard';
import './HelpMenu.scss';

// Обучение перед первым показом — отдельным чанком с роликами: нужно один раз на пользователя.
const loadSupportShareIntro = () => import('@molecules/SupportShareIntro');
const SupportShareIntro = lazy(loadSupportShareIntro);

export interface IHelpMenuProps {
    /** Меню открылось или закрылось: шапке — для очереди онбординг-подсказок */
    onOpenChange?: (opened: boolean) => void;
    /**
     * Видимый шаг подсказки про показ экрана: `0` — у кнопки «Помощь», `1` — карточка у пункта «Показать экран
     * поддержке» в открытом меню; без подсказки — не задан
     */
    screenShareHint?: 0 | 1;
    /** Подсказку закрыли («Понятно», крестик) или показом воспользовались — больше не показывать */
    onScreenShareHintDone?: () => void;
}

/**
 * Единая кнопка «Помощь» в шапке: собирает под одним меню (`Menu` пакета) документацию,
 * техподдержку и новости релиза — раньше это были три отдельные кнопки навбара.
 * Состав зависит от прав; при пустом составе кнопка не рендерится вовсе.
 *
 * Техподдержка и меню взаимоисключающие: открытая поддержка занимает кнопку вместо меню,
 * в DOM в каждый момент только одна панель. Повторный клик по кнопке закрывает поддержку,
 * а не поднимает под ней меню.
 *
 * Показ экрана живёт в `ScreenShareProvider` (корень приложения), полоса — в оболочке; меню его только
 * запускает и останавливает.
 */
const HelpMenu: FC<IHelpMenuProps> = ({ onOpenChange, screenShareHint, onScreenShareHintDone }) => {
    const screenShare = useScreenShareContext();
    const user = useTypedSelector((state) => state.auth.user);
    const { navigate } = useUi();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isSupportOpen, setIsSupportOpen] = useState<boolean>(false);
    const [introOpen, setIntroOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const openChange = useLatest(onOpenChange);
    useEffect(() => {
        openChange.current?.(isOpen);
    }, [isOpen, openChange]);

    // Карточка подсказки — в портале, вне панели меню: нажатие в ней меню считает щелчком мимо и не закрывается.
    const pressInsideCard = useRef(false);
    const onPressInsideCard = useCallback(() => {
        pressInsideCard.current = true;
        window.setTimeout(() => {
            pressInsideCard.current = false;
        });
    }, []);
    const menuOpenChange = useCallback((opened: boolean) => {
        if (!opened && pressInsideCard.current) return;
        setIsOpen(opened);
    }, []);

    const close = useCallback(() => setIsOpen(false), []);
    const openSupport = useCallback(() => {
        setIsOpen(false);
        setIsSupportOpen(true);
    }, []);

    // Кнопка ветки поддержки и кнопка ветки меню — разные элементы: поддержка, закрытая Escape или повторным щелчком,
    // вернула фокус на свою кнопку, а та ушла вместе с веткой. Пропавший фокус — на кнопку меню (щелчок мимо потом
    // переставит его сам).
    const wasSupportOpen = useRef(isSupportOpen);
    useLayoutEffect(() => {
        const closed = wasSupportOpen.current && !isSupportOpen;
        wasSupportOpen.current = isSupportOpen;
        if (closed && (!document.activeElement || document.activeElement === document.body)) {
            buttonRef.current?.focus({ preventScroll: true });
        }
    }, [isSupportOpen]);

    useEffect(() => {
        if (!user.canView.support) {
            return undefined;
        }

        window.addEventListener(OPEN_TECH_SUPPORT_MENU_EVENT, openSupport);

        return () => window.removeEventListener(OPEN_TECH_SUPPORT_MENU_EVENT, openSupport);
    }, [openSupport, user.canView.support]);

    const introPending = screenShare.available && user.id !== undefined && !isSupportShareIntroSeen(user.id);
    // Меню открыто, обучение ещё впереди — чанк с роликами грузится заранее, к нажатию на пункт он уже есть.
    useEffect(() => {
        if (isOpen && introPending) void loadSupportShareIntro().catch(() => undefined);
    }, [introPending, isOpen]);

    // Показ начался не отсюда (приглашение сотрудника, «Техническая поддержка») — обучение уже не к месту.
    useEffect(() => {
        if (screenShare.active) setIntroOpen(false);
    }, [screenShare.active]);

    /**
     * «Показать экран поддержке» (пункт или кнопка карточки подсказки). Первый раз — обучение, его последняя кнопка
     * начинает показ; дальше — сразу. `start()` — прямо в нажатии: выбор вкладки браузер разрешает только по жесту.
     */
    const chooseScreenShare = () => {
        close();
        onScreenShareHintDone?.();
        if (screenShare.active) screenShare.stop();
        else if (introPending) setIntroOpen(true);
        else void screenShare.start();
    };

    // Карточка уходит из-под фокуса, меню остаётся: фокус — на пункт, у которого она стояла.
    const dismissHintCard = () => {
        onScreenShareHintDone?.();
        window.requestAnimationFrame(() => {
            const item = document
                .querySelector('[data-testid="help-item-screen-share"]')
                ?.closest<HTMLElement>('[tabindex]');
            if (!document.activeElement || document.activeElement === document.body) item?.focus();
        });
    };

    // Окно открыто из пункта меню, которого уже нет: фокус после него — на «Помощь», а не в никуда.
    const finishIntro = () => {
        if (user.id !== undefined) markSupportShareIntroSeen(user.id);
        setIntroOpen(false);
        window.requestAnimationFrame(() => {
            if (!document.activeElement || document.activeElement === document.body) buttonRef.current?.focus();
        });
    };

    const startFromIntro = () => {
        finishIntro();
        void screenShare.start();
    };

    const items: IListElement[] = [];

    if (user.canView.documentation) {
        items.push({
            value: 'documentation',
            label: (
                <div className="settings-item" data-testid="help-item-documentation">
                    <MdDescription size="24" />
                    <span>Документация</span>
                </div>
            ),
            handler: () => {
                void navigate(RouteNames.DOCUMENTATION);
            },
        });
    }

    if (user.canView.support) {
        items.push({
            value: 'support',
            label: (
                <div className="settings-item" data-testid="help-item-support">
                    <MdSupportAgent size="24" />
                    <span>Техническая поддержка</span>
                </div>
            ),
            handler: openSupport,
        });
    }

    // Во вкладке «от имени» показа экрана нет: сотрудник не показывает экран самому себе.
    if (screenShare.available) {
        items.push({
            value: 'screenShare',
            label: (
                <ScreenShareHintCard
                    opened={isOpen && screenShareHint === 1}
                    onDismiss={dismissHintCard}
                    onShare={chooseScreenShare}
                    onPressInside={onPressInsideCard}
                >
                    <div className="settings-item" data-testid="help-item-screen-share">
                        <MdScreenShare size="24" />
                        <span>{screenShare.active ? 'Остановить показ экрана' : 'Показать экран поддержке'}</span>
                    </div>
                </ScreenShareHintCard>
            ),
            handler: chooseScreenShare,
        });
    }

    items.push({
        value: 'releasenotes',
        label: (
            <div className="settings-item" data-testid="help-item-releasenotes">
                <MdCampaign size="24" />
                <span>Что нового</span>
            </div>
        ),
        handler: () => {
            void navigate(RouteNames.RELEASENOTES);
        },
    });

    if (items.length === 0) {
        return null;
    }

    const button = (onClick?: () => void) => (
        <Button
            ref={buttonRef}
            variant="header"
            size="md"
            className="rf-help-menu__button"
            data-testid="help-button"
            // aria-expanded / -controls / -haspopup ставит Popover.Target:
            // свои значения он всё равно перебивает, дублировать нечего.
            aria-label="Помощь"
            title="Помощь"
            startAdornment={<MdHelpOutline size="26" />}
            onClick={onClick}
        />
    );

    const closeSupport = () => setIsSupportOpen(false);
    const panel = isSupportOpen ? (
        <SupportDialog target={button(closeSupport)} opened onClose={closeSupport} />
    ) : (
        <Menu
            list={items}
            position="bottom-end"
            width={300}
            opened={isOpen}
            onOpenChange={menuOpenChange}
            // Меню — портал вне обёртки `data-remote-control-exclude`: исключено из удалённого управления само.
            popoverProps={{
                className: `rf-help-menu-dialog ${REMOTE_CONTROL_EXCLUDE_CLASS}`,
                listClassName: 'rf-help-menu__list',
            }}
        >
            {button()}
        </Menu>
    );

    return (
        <>
            <div className="rf-help-menu" data-remote-control-exclude>
                {panel}
                {/* Первый шаг подсказки про показ экрана — у кнопки; щелчок по подсказке открывает меню (второй шаг).
                    Якорь — отдельный слой поверх кнопки: роли поповера подсказки не ложатся на кнопку меню. */}
                <OnboardingHint
                    opened={screenShareHint === 0 && !isOpen && !isSupportOpen}
                    position="bottom"
                    arrowPosition="center"
                    offset={10}
                    icon={<MdScreenShare size={17} />}
                    title="Покажите экран поддержке"
                    text="Не получается разобраться? Специалист посмотрит вашу вкладку и подскажет — прямо здесь."
                    onActivate={() => setIsOpen(true)}
                    onClose={onScreenShareHintDone}
                >
                    <span className="rf-help-menu__hint-anchor" aria-hidden />
                </OnboardingHint>
            </div>
            {introOpen && (
                <Suspense fallback={null}>
                    <SupportShareIntro opened onClose={finishIntro} onStart={startFromIntro} />
                </Suspense>
            )}
        </>
    );
};

export default HelpMenu;
